import PageHeader from "@/components/site/PageHeader";
import { FadeIn, ChapterHeading, EditorialMarquee } from "@/components/motion";
import { VALUES } from "@/config/site";

const APPROACH = [
  {
    num: "01",
    title: "Person before plan",
    text: "We start with who someone is — their home, their people, their routines — and build the plan around that, not the other way around.",
  },
  {
    num: "02",
    title: "Communication as care",
    text: "Clear, timely, plain-spoken communication is not an extra. In a transition, it is the care.",
  },
  {
    num: "03",
    title: "Teams, not handoffs",
    text: "Hospitals, families, and Homevia work as one team. A transition should never feel like being passed along.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="About Homevia"
        lines={["Care, carried", "all the way home."]}
        intro="Homevia Transitional Care exists for one of healthcare's most overlooked moments: the days and weeks after a hospital stay."
        testid="about-heading"
      />

      {/* Story */}
      <section className="px-6 pb-24 lg:px-10">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-12">
          <FadeIn className="lg:col-span-4">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-teal">Our story</p>
            <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink lg:text-5xl">
              Why Homevia began
            </h2>
            <img
              src="/assets/logo-full.png"
              alt="Homevia Transitional Health Care — Bridging the Gap. Supporting What Matters."
              data-testid="about-logo"
              className="mt-10 w-full max-w-xs"
            />
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-7 lg:col-start-6">
            <div className="space-y-6 text-lg leading-relaxed text-olive/75">
              <p>
                Homevia was created around a simple observation: leaving the hospital should feel
                like the start of recovery — yet for many patients and families, it feels like the
                moment support falls away.
              </p>
              <p>
                We believe the transition home deserves the same thoughtfulness as the stay that
                came before it. That belief shapes everything we do: how we listen, how we
                coordinate with care teams, and how we stand beside families while home becomes a
                place of healing again.
              </p>
              <p className="font-display italic text-xl text-teal-deep">
                Our full story — including the people behind Homevia — will be shared here soon.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Mission */}
      <section className="px-6 pb-24 lg:px-10">
        <FadeIn className="mx-auto max-w-7xl">
          <div className="grain relative overflow-hidden rounded-[2.5rem] bg-olive px-8 py-16 text-center sm:px-16 lg:py-20">
            <div className="relative z-[2]">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-sage">Our mission</p>
              <p className="mx-auto mt-6 max-w-3xl font-serif text-3xl leading-snug text-cream lg:text-4xl">
                To make the transition from hospital to home feel safe, coordinated, and human —
                for patients, and for the people who love them.
              </p>
            </div>
          </div>
        </FadeIn>
      </section>

      <EditorialMarquee items={["Compassion", "Clarity", "Coordination", "Respect"]} testid="about-marquee" />

      {/* Values */}
      <section className="px-6 py-24 lg:px-10 lg:py-32">
        <div className="mx-auto max-w-7xl">
          <FadeIn>
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-teal">What we stand for</p>
            <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink lg:text-5xl">Our values</h2>
          </FadeIn>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <FadeIn key={v.title} delay={i * 0.1}>
                <div className="h-full rounded-3xl border border-olive/10 bg-white p-8 transition-all duration-500 hover:scale-[1.02] hover:border-teal/50">
                  <span className="font-display italic text-lg text-teal">(0{i + 1})</span>
                  <h3 className="mt-6 font-serif text-2xl text-ink">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-olive/70">{v.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Care approach */}
      <section className="bg-sand/50 px-6 py-24 lg:px-10 lg:py-32">
        <div className="mx-auto max-w-7xl">
          <FadeIn>
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-teal">How we work</p>
            <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink lg:text-5xl">
              Our care approach
            </h2>
          </FadeIn>
          <div className="mt-14">
            {APPROACH.map((a, i) => (
              <div
                key={a.num}
                className={`grid gap-6 border-t border-olive/10 py-12 lg:grid-cols-12 lg:gap-12 ${
                  i === APPROACH.length - 1 ? "border-b" : ""
                }`}
              >
                <div className="lg:col-span-4">
                  <ChapterHeading number={a.num} title={a.title} />
                </div>
                <FadeIn delay={0.15} className="lg:col-span-7 lg:col-start-6">
                  <p className="max-w-2xl text-lg leading-relaxed text-olive/75">{a.text}</p>
                </FadeIn>
              </div>
            ))}
          </div>
        </div>
      </section>



    </div>
  );
}